import { useCallback, useRef } from 'react';

/** Set on a scroller while its kept place is being given back (`shell/useKeepPosition.ts`). */
export const RESTORING_POSITION_ATTRIBUTE = 'data-restoring-position';

/**
 * Keeps the reader's place in a scrolling pane when content above them changes (I-0781, QA 272).
 *
 * A comment made elsewhere lands in the comment trail; a reader who had scrolled past the trail —
 * to the attachments, the history — had it inserted above them and was pushed down 104–128 px per
 * comment. Chromium's native scroll anchoring (`overflow-anchor`) compensated on some hosts and not
 * others, and WebKit — the Mac app's engine — has none at all. So the app does it, the same way in
 * every engine:
 *
 * - **What is held.** On every scroll the pane records two elements with their offsets: the one at
 *   its top edge and the one at its bottom edge (descending from the pane to the deepest element
 *   under that line). A reader mid-pane reads from the top down, so the top one is held. A reader
 *   scrolled to the end reads what is last, so the bottom one is: a comment landing inside the view
 *   then moves the older text above it up, and leaves what they were reading where it was.
 * - **When.** After any change inside the pane — a DOM mutation, or an element changing size — the
 *   held element's offset is measured again and `scrollTop` moves by the difference, before the
 *   frame is painted (a `MutationObserver` callback runs as a microtask after React commits; a
 *   `ResizeObserver` callback runs after layout and before paint). Content that changes below the
 *   held element moves nothing.
 * - **A scroll nobody has reported yet is kept.** A script's scroll — `scrollIntoView`, a focus, a
 *   `scrollTop` write — moves the pane at once, but its `scroll` event, and so the new record, waits
 *   for the next frame. A change landing in between found the record of the old place, and moved the
 *   pane back to it: the history address's `scrollIntoView` undone by the next render. So a change
 *   that finds the pane somewhere other than where it was recorded holds nothing and records the new
 *   place; the one move that is not a scroll, the browser pulling `scrollTop` down to the new end
 *   when the content shrinks, is still held.
 * - Native anchoring is switched off on the pane, so the two never both correct one change.
 * - At the very top nothing is held, as native anchoring does not: a reader who has not scrolled
 *   sees what arrives above them.
 */
export function attachScrollAnchor(pane: HTMLElement): () => void {
  pane.style.overflowAnchor = 'none';
  let anchor: Element | null = null;
  let offset = 0;
  // Where the pane was when `anchor` was recorded.
  let recordedAt = 0;

  const paneTop = () => pane.getBoundingClientRect().top;

  function record(): void {
    anchor = null;
    offset = 0;
    recordedAt = pane.scrollTop;
    if (pane.scrollTop <= 0) return;
    const box = pane.getBoundingClientRect();
    const atEnd = pane.scrollHeight - pane.clientHeight - pane.scrollTop <= 1;
    anchor = atEnd
      ? elementAt(pane, box.bottom - 2, 'above')
      : elementAt(pane, box.top + 1, 'below');
    offset = anchor ? anchor.getBoundingClientRect().top - box.top : 0;
  }

  /** The pane has not moved since the record, or only by the browser clamping it to a new end. */
  function stillWhereRecorded(): boolean {
    const top = pane.scrollTop;
    if (top === recordedAt) return true;
    return top < recordedAt && pane.scrollHeight - pane.clientHeight - top <= 1;
  }

  function hold(): void {
    // I-1304 (IL-17): a pane whose place is being given back is the restore's to place, not ours —
    // the content it draws is not what the reader saw, so holding it would carry them past it.
    if (pane.hasAttribute(RESTORING_POSITION_ATTRIBUTE)) {
      record();
      return;
    }
    if (anchor?.isConnected && pane.contains(anchor) && stillWhereRecorded()) {
      const drift = anchor.getBoundingClientRect().top - paneTop() - offset;
      if (Math.abs(drift) >= 0.5) pane.scrollTop += drift;
    }
    record();
  }

  const resizes = new ResizeObserver(hold);
  const observeChildren = () => {
    for (const child of Array.from(pane.children)) resizes.observe(child);
  };
  const mutations = new MutationObserver((records) => {
    if (records.some((entry) => entry.target === pane && entry.type === 'childList')) {
      observeChildren();
    }
    hold();
  });

  record();
  observeChildren();
  mutations.observe(pane, { childList: true, subtree: true, characterData: true });
  pane.addEventListener('scroll', record, { passive: true });

  return () => {
    pane.removeEventListener('scroll', record);
    mutations.disconnect();
    resizes.disconnect();
    pane.style.overflowAnchor = '';
  };
}

/** Out of flow: its box does not move with the content, so it cannot say where the content went. */
const UNANCHORABLE = new Set(['sticky', 'fixed', 'absolute']);

/**
 * The deepest in-flow element of `pane` at the horizontal line `y`. Where the line falls in a gap
 * between two children, the one the reader sees on that side of it: the next one below the line for
 * the top edge (`below`), the last one above it for the bottom edge (`above`).
 */
function elementAt(pane: HTMLElement, y: number, side: 'below' | 'above'): Element | null {
  let node: Element = pane;
  for (let depth = 0; depth < 64; depth += 1) {
    const children = Array.from(node.children).filter((child) => {
      const box = child.getBoundingClientRect();
      return box.height > 0 && !UNANCHORABLE.has(getComputedStyle(child).position);
    });
    const boxes = children.map((child) => child.getBoundingClientRect());
    let index = boxes.findIndex((box) => box.top <= y && box.bottom > y);
    if (index < 0) {
      index =
        side === 'below'
          ? boxes.findIndex((box) => box.top > y)
          : boxes.map((box) => box.bottom <= y).lastIndexOf(true);
    }
    if (index < 0) break;
    node = children[index];
    // An icon's parts are not text a reader reads; the icon itself is the element.
    if (node instanceof SVGElement) break;
  }
  return node === pane ? null : node;
}

/**
 * The same, as a ref callback for the pane that scrolls. Stable across renders, so React attaches
 * once per element and detaches when the element goes.
 */
export function useScrollAnchor<T extends HTMLElement>(): (element: T | null) => void {
  const detach = useRef<(() => void) | null>(null);
  return useCallback((element: T | null) => {
    detach.current?.();
    detach.current = element ? attachScrollAnchor(element) : null;
  }, []);
}
